import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Button,
  Stack,
  Box,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  IconButton,
  Chip
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useCollection } from "../hooks/useCollection";
import { Worker, WorkersConfig, WorkerTransaction } from "../interfaces/Worker";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { Users } from "../interfaces/Users";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { PremiumDataGrid } from "../components/PremiumDataGrid";

import WorkerDialog from "../components/WorkerDialog";
import AbonoDialog from "../components/AbonoDialog";
import CargoDialog from "../components/CargoDialog";
import WorkerHistoryDialog from "../components/WorkerHistoryDialog";
import { useAuth } from "../hooks/useAuth";

const DEFAULT_MAX_DEBT = 50000;

const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  if (!role) return "default";
  const r = role.toLowerCase();
  if (r.includes('admin')) return 'error';
  if (r.includes('cajero principal')) return 'secondary';
  if (r.includes('cajero')) return 'primary';
  if (r.includes('prep')) return 'warning';
  if (r.includes('domi')) return 'success';
  return 'default';
};

export default function WorkersPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: workers, createItem, updateItem, setItem, deleteItem } =
    useCollection<Worker>(restaurantId!, "workers");
  const { data: usersData } = useCollection<Users>(restaurantId!, "users");

  const combinedWorkers = useMemo(() => {
    const list = [...workers];
    if (usersData) {
      usersData.forEach((user) => {
        const uId = user.uid || (user as any).id;
        if (!uId) return;
        const exists = list.find((w) => w.id === uId);
        // Si no existe un Worker asociado, lo agregamos a la lista con deuda 0
        if (!exists && user.name) {
          list.push({
            id: uId,
            name: user.name,
            phone: user.email || "",
            currentDebt: 0,
            isActive: true,
            history: [],
            role: user.role || "Sin Rol",
          });
        } else if (exists) {
          // Si ya existe, actualizamos su rol de usersData si no lo tiene
          exists.role = user.role || exists.role || "Sin Rol";
        }
      });
    }
    return list;
  }, [workers, usersData]);

  const [config, setConfig] = useState<WorkersConfig>({ globalMaxDebt: DEFAULT_MAX_DEBT });
  const [editingMaxDebt, setEditingMaxDebt] = useState(false);
  const [tempMaxDebt, setTempMaxDebt] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  const { canCreate, canEdit, canDelete } = useAuth();

  // Modals state
  const [workerDialogOpen, setWorkerDialogOpen] = useState(false);
  const [currentWorker, setCurrentWorker] = useState<Partial<Worker> | null>(null);

  const [abonoDialogOpen, setAbonoDialogOpen] = useState(false);
  const [abonoWorker, setAbonoWorker] = useState<Worker | null>(null);

  const [cargoDialogOpen, setCargoDialogOpen] = useState(false);
  const [cargoWorker, setCargoWorker] = useState<Worker | null>(null);

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyWorker, setHistoryWorker] = useState<Worker | null>(null);

  useEffect(() => {
    if (!restaurantId) return;
    const configRef = doc(db, "restaurants", restaurantId, "settings", "workers_config");
    
    const unsubscribe = onSnapshot(configRef, (docSnap) => {
      if (docSnap.exists()) {
        setConfig(docSnap.data() as WorkersConfig);
      } else {
        setDoc(configRef, { globalMaxDebt: DEFAULT_MAX_DEBT }).catch(console.error);
      }
    });

    return () => unsubscribe();
  }, [restaurantId]);

  const filteredWorkers = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return combinedWorkers;
    return combinedWorkers.filter(w => 
      (w.name && w.name.toLowerCase().includes(q)) || 
      (w.role && w.role.toLowerCase().includes(q))
    );
  }, [combinedWorkers, searchQuery]);

  const handleUpdateMaxDebt = async () => {
    if (!restaurantId) return;
    const val = Number(tempMaxDebt);
    if (val > 0) {
      const configRef = doc(db, "restaurants", restaurantId, "settings", "workers_config");
      await setDoc(configRef, { globalMaxDebt: val }, { merge: true });
      setEditingMaxDebt(false);
    }
  };

  const handleSaveWorker = async (data: Partial<Worker>) => {
    if (data.id) {
      await setItem(data.id, {
        name: data.name!,
        phone: data.phone || "",
        currentDebt: data.currentDebt ?? 0,
        isActive: data.isActive ?? true,
        history: data.history || []
      });
    } else {
      await createItem({
        name: data.name!,
        phone: data.phone || "",
        currentDebt: 0,
        isActive: true,
        history: []
      });
    }
  };

  const handleProcessAbono = async (amount: number, paymentMethod: string) => {
    if (!abonoWorker || !abonoWorker.id) return;
    
    const newDebt = Math.max(0, abonoWorker.currentDebt - amount);
    
    const newTx: WorkerTransaction = {
      id: Date.now().toString() + Math.random().toString(36).substring(7),
      type: 'abono',
      amount: amount,
      date: Date.now(),
      detail: 'Abono de deuda',
      paymentMethod: paymentMethod
    };

    const newHistory = [...(abonoWorker.history || []), newTx];

    await setItem(abonoWorker.id, {
      name: abonoWorker.name,
      phone: abonoWorker.phone || "",
      isActive: abonoWorker.isActive ?? true,
      currentDebt: newDebt,
      history: newHistory
    });
  };

  const handleProcessCargo = async (amount: number, detail: string) => {
    if (!cargoWorker || !cargoWorker.id) return;
    
    const newDebt = cargoWorker.currentDebt + amount;
    
    const newTx: WorkerTransaction = {
      id: Date.now().toString() + Math.random().toString(36).substring(7),
      type: 'cargo',
      amount: amount,
      date: Date.now(),
      detail: detail || 'Cargo manual de deuda',
      paymentMethod: 'N/A'
    };

    const newHistory = [...(cargoWorker.history || []), newTx];

    await setItem(cargoWorker.id, {
      name: cargoWorker.name,
      phone: cargoWorker.phone || "",
      isActive: cargoWorker.isActive ?? true,
      currentDebt: newDebt,
      history: newHistory
    });
  };

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(val);

  const columns: GridColDef[] = [
    { field: "name", headerName: "Nombre", flex: 1.5 },
    { 
      field: "role", 
      headerName: "Rol", 
      flex: 1,
      renderCell: (params) => {
        return (
          <Chip label={params.value || "N/A"} size="small" color={getRoleColor(params.value || "")} sx={{ fontWeight: 'bold' }} />
        );
      }
    },
    { field: "phone", headerName: "Teléfono", flex: 1 },
    { 
      field: "currentDebt", 
      headerName: "Deuda Actual", 
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack direction="row" alignItems="center" spacing={1} height="100%">
            <Typography fontWeight={800} color={"warning.main"}>
              {formatCurrency(params.value)}
            </Typography>
          </Stack>
        );
      }
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 280,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as Worker;
        return (
          <Stack direction="row" spacing={1} alignItems="center" height="100%">
            <Button
              size="small"
              variant="contained"
              color="warning"
              disabled={!canEdit}
              onClick={(e) => {
                e.stopPropagation();
                setCargoWorker(row);
                setCargoDialogOpen(true);
              }}
            >
              Cargar
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={!canEdit}
              onClick={(e) => {
                e.stopPropagation();
                setAbonoWorker(row);
                setAbonoDialogOpen(true);
              }}
            >
              Abonar
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                setHistoryWorker(row);
                setHistoryDialogOpen(true);
              }}
            >
              Historial
            </Button>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box p={isMobile ? 1 : 3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>👨‍🔧 Trabajadores y Crédito</Typography>
        </Box>
        <TextField
          placeholder="Buscar por nombre o rol..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
        />

        {canCreate && (
          <Button
            variant="contained"
            startIcon={<span>➕</span>}
            onClick={() => {
              setCurrentWorker(null);
              setWorkerDialogOpen(true);
            }}
            sx={{ borderRadius: 2, px: 4, py: 1.5, height: 'fit-content' }}
          >
            Agregar Trabajador
          </Button>
        )}
      </Stack>

      {/* DESKTOP TABLE */}
      {!isMobile && (
        <Box sx={{ height: 600, width: "100%" }}>
          <PremiumDataGrid
            rows={filteredWorkers}
            columns={columns}
            getRowId={(row) => row.id!}
            pageSizeOptions={[10, 20]}
            onRowDoubleClick={(p: GridRowParams) => {
              setCurrentWorker(p.row as Worker);
              setWorkerDialogOpen(true);
            }}
          />
        </Box>
      )}

      {/* MOBILE CARDS */}
      {isMobile && (
        <Stack spacing={2}>
          {filteredWorkers.length === 0 ? (
            <Typography color="text.secondary">No se encontraron trabajadores.</Typography>
          ) : (
            filteredWorkers.map(worker => {
              return (
                <Card key={worker.id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle1" fontWeight={700}>{worker.name}</Typography>
                      {worker.role && <Chip label={worker.role} size="small" color={getRoleColor(worker.role)} sx={{ fontWeight: 'bold' }} />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={1}>{worker.phone || "Sin contacto"}</Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" color="text.secondary">Deuda Actual</Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6" fontWeight={800} color={"warning.main"}>
                          {formatCurrency(worker.currentDebt)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack spacing={1} mt={1}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          disabled={!canEdit}
                          onClick={() => {
                            setCargoWorker(worker);
                            setCargoDialogOpen(true);
                          }}
                          sx={{ flex: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                        >
                          Cargar
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={!canEdit}
                          onClick={() => {
                            setAbonoWorker(worker);
                            setAbonoDialogOpen(true);
                          }}
                          sx={{ flex: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                        >
                          Abonar
                        </Button>
                      </Stack>
                      <Button 
                        size="small"
                        variant="outlined" 
                        color="info" 
                        onClick={() => { setHistoryWorker(worker); setHistoryDialogOpen(true); }} 
                        fullWidth
                        sx={{ fontSize: '0.8rem' }}
                      >
                        Historial
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      )}

      {/* Modals */}
      <WorkerDialog
        open={workerDialogOpen}
        onClose={() => setWorkerDialogOpen(false)}
        initial={currentWorker}
        onSubmit={handleSaveWorker}
      />

      <CargoDialog
        open={cargoDialogOpen}
        onClose={() => setCargoDialogOpen(false)}
        workerName={cargoWorker?.name}
        currentDebt={cargoWorker?.currentDebt || 0}
        onSubmit={handleProcessCargo}
      />
      
      <AbonoDialog
        open={abonoDialogOpen}
        onClose={() => setAbonoDialogOpen(false)}
        workerName={abonoWorker?.name}
        currentDebt={abonoWorker?.currentDebt || 0}
        onSubmit={handleProcessAbono}
      />

      <WorkerHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        workerName={historyWorker?.name}
        history={historyWorker?.history}
      />
    </Box>
  );
}
